<!-- The packages you offer: hours or months, a Zelle/cash price and a card price. -->
<script lang="ts">
  import { shows } from "../../settings/index.ts";
  import * as TT from "../../settings/tutoring.ts";
  import { packageSavings, type TutoringPackage } from "@split/core";
  import { tutoring } from "../../lib/tutoring.svelte.ts";
  import { app } from "../../lib/store.svelte.ts";
  import { centsInput, formatCents, readMoney } from "../../lib/format.ts";

  let f = $state({ name: "", unit: "hours" as TutoringPackage["unit"], amount: "10", cash: "", card: "" });
  let editing = $state<TutoringPackage | null>(null);
  let compare = $state("40");

  async function save(e: SubmitEvent) {
    e.preventDefault();
    const cash = readMoney(f.cash);
    const card = f.card.trim() ? readMoney(f.card) : null;
    const amount = Number(f.amount);
    if (cash === null || !amount || (f.card.trim() && card === null)) return app.toast("Enter the amount and prices", "error");
    const body = {
      name: f.name.trim() || `${amount} ${f.unit}`,
      unit: f.unit,
      amount,
      priceCashCents: cash,
      priceCardCents: card ?? 0,
    };
    if (editing) await tutoring.update("packages", editing.id, body, "Saved");
    else await tutoring.add("packages", body, "Package added");
    editing = null;
    f = { name: "", unit: "hours", amount: "10", cash: "", card: "" };
  }
  function edit(p: TutoringPackage) {
    editing = p;
    f = { name: p.name, unit: p.unit, amount: String(p.amount), cash: centsInput(p.priceCashCents), card: p.priceCardCents ? centsInput(p.priceCardCents) : "" };
  }
  const rate = $derived(readMoney(compare) ?? 0);
</script>

{#if shows(TT.PACKAGES_NOTE)}<p class="muted">{TT.PACKAGES_NOTE}</p>{/if}
<form class="panel" onsubmit={save}>
  <div class="grid">
    <label class="field"><span>Name</span><input class="input" bind:value={f.name} placeholder="10-hour pack" /></label>
    <label class="field"
      ><span>Of</span>
      <select class="input" bind:value={f.unit}><option value="hours">Hours</option><option value="months">Months</option></select></label
    >
    <label class="field"><span>How many</span><input class="input" bind:value={f.amount} inputmode="decimal" /></label>
    <label class="field"><span>Price, Zelle or cash</span><input class="input money" bind:value={f.cash} inputmode="decimal" placeholder="380" /></label>
    <label class="field"><span>Price, card</span><input class="input money" bind:value={f.card} inputmode="decimal" placeholder="427.50" /></label>
  </div>
  <div class="btns">
    <button class="btn primary">{editing ? "Save package" : "Add package"}</button>
    {#if editing}<button type="button" class="btn quiet" onclick={() => (editing = null)}>Cancel</button>{/if}
  </div>
</form>

<label class="cmp muted">Savings compared with <input class="input money" bind:value={compare} inputmode="decimal" /> /hr</label>
<div class="list">
  {#each tutoring.packages as p (p.id)}
    {@const save = packageSavings(p, rate)}
    <div class="row" class:off={!p.active}>
      <span class="who">
        <strong>{p.name}</strong>
        <span class="muted small">{p.amount} {p.unit}{p.unit === "hours" ? ` · ${formatCents(Math.round(p.priceCashCents / p.amount))}/hr` : ""}</span>
      </span>
      <span class="price">
        <span class="cash">{formatCents(p.priceCashCents)} <span class="small">Zelle/cash</span></span>
        {#if p.priceCardCents}<span class="muted small">{formatCents(p.priceCardCents)} card</span>{/if}
      </span>
      {#if save}<span class="save">Saves {save}%</span>{:else}<span></span>{/if}
      <span class="ops">
        <button class="btn small quiet" onclick={() => edit(p)}>Edit</button>
        <button class="btn small quiet" onclick={() => tutoring.update("packages", p.id, { active: !p.active })}>{p.active ? "Retire" : "Offer again"}</button>
      </span>
    </div>
  {:else}
    <div class="empty">No packages yet.</div>
  {/each}
</div>

<style>
  .panel {
    display: grid;
    gap: 10px;
    padding: 16px;
    margin: 10px 0 14px;
    border-radius: var(--r-lg);
    background: var(--surface);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
  }

  .btns {
    display: flex;
    gap: 8px;
  }

  .cmp {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    margin-bottom: 8px;
    font-size: var(--t-sm);
  }

  .cmp .input {
    width: 80px;
    min-height: 30px;
    padding: 2px 8px;
  }

  .row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto auto;
    gap: 12px;
    align-items: center;
    padding: 12px 16px;
    background: var(--surface);
  }

  .row.off {
    opacity: 0.55;
  }

  .who,
  .price {
    display: grid;
  }

  .price {
    justify-items: end;
  }

  .cash {
    font-weight: 650;
    color: var(--ok);
  }

  .save {
    padding: 2px 8px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--ok) 14%, transparent);
    color: var(--ok);
    font-size: var(--t-xs);
    font-weight: 700;
  }

  .small {
    font-size: var(--t-xs);
  }

  @media (max-width: 640px) {
    .row {
      grid-template-columns: 1fr auto;
    }
  }
</style>
